// Dependency-free, browser-local "light" JavaScript obfuscator + a companion
// deobfuscator. This is a conservative, token-based transform (NOT a full
// parser). It is meant to make source harder to read at a glance — it is not
// real protection and can be reversed. All logic here is pure, text-in /
// text-out, and safe to run in the browser or in Node.

// ---------------------------------------------------------------------------
// Tokenizer
// ---------------------------------------------------------------------------

type JsTokenType =
  | 'ws'
  | 'line-comment'
  | 'block-comment'
  | 'string'
  | 'template'
  | 'regex'
  | 'number'
  | 'ident'
  | 'punct';

interface JsToken {
  type: JsTokenType;
  value: string;
}

const IDENT_START = /[A-Za-z_$]/;
const IDENT_PART = /[A-Za-z0-9_$]/;

// Multi-char operators, longest first, so we tokenize `===` before `==`.
const MULTI_PUNCT = [
  '>>>=',
  '===',
  '!==',
  '**=',
  '>>>',
  '...',
  '<<=',
  '>>=',
  '&&=',
  '||=',
  '??=',
  '=>',
  '==',
  '!=',
  '<=',
  '>=',
  '&&',
  '||',
  '??',
  '?.',
  '++',
  '--',
  '+=',
  '-=',
  '*=',
  '/=',
  '%=',
  '&=',
  '|=',
  '^=',
  '<<',
  '>>',
  '**',
];

// Keywords after which a `/` begins a regular expression rather than division.
const REGEX_PRECEDING_KEYWORDS = new Set([
  'return',
  'typeof',
  'instanceof',
  'in',
  'of',
  'new',
  'delete',
  'void',
  'do',
  'else',
  'yield',
  'await',
  'case',
]);

function regexCanFollow(prev: JsToken | null): boolean {
  if (!prev) return true;
  if (prev.type === 'punct') {
    return prev.value !== ')' && prev.value !== ']';
  }
  if (prev.type === 'ident') {
    return REGEX_PRECEDING_KEYWORDS.has(prev.value);
  }
  return false;
}

function readString(src: string, start: number): number {
  const quote = src[start];
  let i = start + 1;
  const n = src.length;
  while (i < n) {
    const c = src[i];
    if (c === '\\') {
      i += 2;
      continue;
    }
    if (c === quote) return i + 1;
    if (c === '\n') return i; // unterminated; stop at newline
    i += 1;
  }
  return n;
}

function readTemplate(src: string, start: number): number {
  let i = start + 1;
  const n = src.length;
  let braceDepth = 0;
  while (i < n) {
    const c = src[i];
    if (c === '\\') {
      i += 2;
      continue;
    }
    if (braceDepth === 0 && c === '`') return i + 1;
    if (c === '$' && src[i + 1] === '{') {
      braceDepth += 1;
      i += 2;
      continue;
    }
    if (braceDepth > 0 && c === '{') braceDepth += 1;
    if (braceDepth > 0 && c === '}') braceDepth -= 1;
    i += 1;
  }
  return n;
}

function readRegex(src: string, start: number): number {
  let i = start + 1;
  const n = src.length;
  let inClass = false;
  while (i < n) {
    const c = src[i];
    if (c === '\\') {
      i += 2;
      continue;
    }
    if (c === '\n') return start + 1; // not a regex; bail after the slash
    if (c === '[') inClass = true;
    else if (c === ']') inClass = false;
    else if (c === '/' && !inClass) {
      i += 1;
      while (i < n && IDENT_PART.test(src[i])) i += 1; // flags
      return i;
    }
    i += 1;
  }
  return n;
}

function readNumber(src: string, start: number): number {
  let i = start;
  const n = src.length;
  if (src[i] === '0' && (src[i + 1] === 'x' || src[i + 1] === 'X')) {
    i += 2;
    while (i < n && /[0-9a-fA-F_]/.test(src[i])) i += 1;
    if (src[i] === 'n') i += 1;
    return i;
  }
  if (
    src[i] === '0' &&
    (src[i + 1] === 'b' || src[i + 1] === 'B' || src[i + 1] === 'o' || src[i + 1] === 'O')
  ) {
    i += 2;
    while (i < n && /[0-9a-fA-F_]/.test(src[i])) i += 1;
    if (src[i] === 'n') i += 1;
    return i;
  }
  while (i < n && /[0-9_]/.test(src[i])) i += 1;
  if (src[i] === '.') {
    i += 1;
    while (i < n && /[0-9_]/.test(src[i])) i += 1;
  }
  if (src[i] === 'e' || src[i] === 'E') {
    i += 1;
    if (src[i] === '+' || src[i] === '-') i += 1;
    while (i < n && /[0-9_]/.test(src[i])) i += 1;
  }
  if (src[i] === 'n') i += 1;
  return i;
}

function tokenizeJs(src: string): JsToken[] {
  const tokens: JsToken[] = [];
  let i = 0;
  const n = src.length;
  let prevSig: JsToken | null = null;

  const push = (type: JsTokenType, value: string) => {
    const token: JsToken = { type, value };
    tokens.push(token);
    if (type !== 'ws' && type !== 'line-comment' && type !== 'block-comment') {
      prevSig = token;
    }
  };

  while (i < n) {
    const c = src[i];

    if (/\s/.test(c)) {
      let j = i + 1;
      while (j < n && /\s/.test(src[j])) j += 1;
      push('ws', src.slice(i, j));
      i = j;
      continue;
    }

    if (c === '/' && src[i + 1] === '/') {
      let j = src.indexOf('\n', i);
      if (j === -1) j = n;
      push('line-comment', src.slice(i, j));
      i = j;
      continue;
    }

    if (c === '/' && src[i + 1] === '*') {
      let j = src.indexOf('*/', i + 2);
      j = j === -1 ? n : j + 2;
      push('block-comment', src.slice(i, j));
      i = j;
      continue;
    }

    if (c === '"' || c === "'") {
      const j = readString(src, i);
      push('string', src.slice(i, j));
      i = j;
      continue;
    }

    if (c === '`') {
      const j = readTemplate(src, i);
      push('template', src.slice(i, j));
      i = j;
      continue;
    }

    if (c === '/' && regexCanFollow(prevSig)) {
      const j = readRegex(src, i);
      push('regex', src.slice(i, j));
      i = j;
      continue;
    }

    if (/[0-9]/.test(c) || (c === '.' && /[0-9]/.test(src[i + 1] ?? ''))) {
      const j = readNumber(src, i);
      push('number', src.slice(i, j));
      i = j;
      continue;
    }

    if (IDENT_START.test(c)) {
      let j = i + 1;
      while (j < n && IDENT_PART.test(src[j])) j += 1;
      push('ident', src.slice(i, j));
      i = j;
      continue;
    }

    let matched: string | null = null;
    for (const op of MULTI_PUNCT) {
      if (src.startsWith(op, i)) {
        matched = op;
        break;
      }
    }
    if (matched) {
      push('punct', matched);
      i += matched.length;
      continue;
    }

    push('punct', c);
    i += 1;
  }

  return tokens;
}

// ---------------------------------------------------------------------------
// Helpers shared by the transforms
// ---------------------------------------------------------------------------

const DECL_KEYWORDS = new Set(['const', 'let', 'var', 'function', 'class']);

const RESERVED = new Set([
  'break',
  'case',
  'catch',
  'class',
  'const',
  'continue',
  'debugger',
  'default',
  'delete',
  'do',
  'else',
  'export',
  'extends',
  'false',
  'finally',
  'for',
  'function',
  'if',
  'import',
  'in',
  'instanceof',
  'new',
  'null',
  'return',
  'super',
  'switch',
  'this',
  'throw',
  'true',
  'try',
  'typeof',
  'var',
  'void',
  'while',
  'with',
  'let',
  'static',
  'yield',
  'await',
  'async',
  'of',
  'as',
  'from',
  'get',
  'set',
]);

function isSig(token: JsToken): boolean {
  return token.type !== 'ws' && token.type !== 'line-comment' && token.type !== 'block-comment';
}

function decodeStringLiteral(literal: string): string {
  const quote = literal[0];
  const inner = literal.slice(1, literal.endsWith(quote) ? -1 : undefined);
  let out = '';
  let i = 0;
  const n = inner.length;
  while (i < n) {
    const c = inner[i];
    if (c !== '\\') {
      out += c;
      i += 1;
      continue;
    }
    const next = inner[i + 1];
    if (next === 'n') {
      out += '\n';
      i += 2;
    } else if (next === 't') {
      out += '\t';
      i += 2;
    } else if (next === 'r') {
      out += '\r';
      i += 2;
    } else if (next === 'b') {
      out += '\b';
      i += 2;
    } else if (next === 'f') {
      out += '\f';
      i += 2;
    } else if (next === 'v') {
      out += '\v';
      i += 2;
    } else if (next === '0' && !/[0-9]/.test(inner[i + 2] ?? '')) {
      out += '\0';
      i += 2;
    } else if (next === 'x') {
      const hex = inner.slice(i + 2, i + 4);
      if (/^[0-9a-fA-F]{2}$/.test(hex)) {
        out += String.fromCharCode(parseInt(hex, 16));
        i += 4;
      } else {
        out += next;
        i += 2;
      }
    } else if (next === 'u') {
      if (inner[i + 2] === '{') {
        const end = inner.indexOf('}', i + 3);
        const hex = end === -1 ? '' : inner.slice(i + 3, end);
        if (end !== -1 && /^[0-9a-fA-F]+$/.test(hex)) {
          out += String.fromCodePoint(parseInt(hex, 16));
          i = end + 1;
        } else {
          out += next;
          i += 2;
        }
      } else {
        const hex = inner.slice(i + 2, i + 6);
        if (/^[0-9a-fA-F]{4}$/.test(hex)) {
          out += String.fromCharCode(parseInt(hex, 16));
          i += 6;
        } else {
          out += next;
          i += 2;
        }
      }
    } else if (next === '\n') {
      i += 2; // line continuation
    } else {
      out += next;
      i += 2;
    }
  }
  return out;
}

function hexEncodeString(value: string, quote: string): string {
  let out = quote;
  for (const ch of value) {
    const code = ch.codePointAt(0) ?? 0;
    if (code < 256) {
      out += '\\x' + code.toString(16).padStart(2, '0');
    } else if (code <= 0xffff) {
      out += '\\u' + code.toString(16).padStart(4, '0');
    } else {
      out += '\\u{' + code.toString(16) + '}';
    }
  }
  return out + quote;
}

const B64_CHARS = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789+/';

function utf8Base64(value: string): string {
  const bytes: number[] = [];
  for (const ch of value) {
    let code = ch.codePointAt(0) ?? 0;
    if (code < 0x80) {
      bytes.push(code);
    } else if (code < 0x800) {
      bytes.push(0xc0 | (code >> 6), 0x80 | (code & 0x3f));
    } else if (code < 0x10000) {
      bytes.push(0xe0 | (code >> 12), 0x80 | ((code >> 6) & 0x3f), 0x80 | (code & 0x3f));
    } else {
      bytes.push(
        0xf0 | (code >> 18),
        0x80 | ((code >> 12) & 0x3f),
        0x80 | ((code >> 6) & 0x3f),
        0x80 | (code & 0x3f),
      );
    }
    code = 0;
  }
  let out = '';
  for (let i = 0; i < bytes.length; i += 3) {
    const b0 = bytes[i];
    const b1 = bytes[i + 1];
    const b2 = bytes[i + 2];
    out += B64_CHARS[b0 >> 2];
    out += B64_CHARS[((b0 & 0x03) << 4) | ((b1 ?? 0) >> 4)];
    out += b1 === undefined ? '=' : B64_CHARS[((b1 & 0x0f) << 2) | ((b2 ?? 0) >> 6)];
    out += b2 === undefined ? '=' : B64_CHARS[b2 & 0x3f];
  }
  return out;
}

const BASE64_DECODER =
  "function _0xdec(s){return decodeURIComponent(Array.prototype.map.call(atob(s),function(c){return '%'+('00'+c.charCodeAt(0).toString(16)).slice(-2)}).join(''));}\n";

const DEAD_CODE =
  "var _0xd1=['\\x64\\x65\\x61\\x64'];function _0xd2(a,b){return a^b;}if(false){_0xd2(_0xd1.length,0x1);}\n";

// ---------------------------------------------------------------------------
// Local-identifier renaming (best effort, conservative)
// ---------------------------------------------------------------------------

function collectSafeRenames(tokens: JsToken[]): Map<string, string> {
  const sig = tokens.filter(isSig);
  const declared = new Set<string>();

  for (let k = 0; k < sig.length; k += 1) {
    const tok = sig[k];
    // `const foo`, `let foo`, `var foo`, `function foo`, `class foo`
    if (tok.type === 'ident' && DECL_KEYWORDS.has(sig[k - 1]?.value)) {
      if (!RESERVED.has(tok.value)) declared.add(tok.value);
    }
    // Function parameter lists: `function ( a, b )`.
    if (tok.type === 'ident' && tok.value === 'function') {
      let p = k + 1;
      while (p < sig.length && sig[p].value !== '(') p += 1;
      if (sig[p]?.value === '(') {
        let depth = 0;
        let expectName = true;
        for (let q = p; q < sig.length; q += 1) {
          const v = sig[q].value;
          if (v === '(' || v === '[' || v === '{') {
            depth += 1;
            continue;
          }
          if (v === ')' || v === ']' || v === '}') {
            depth -= 1;
            if (depth === 0) break;
            continue;
          }
          if (depth === 1 && v === ',') {
            expectName = true;
            continue;
          }
          if (depth === 1 && expectName && sig[q].type === 'ident' && !RESERVED.has(v)) {
            declared.add(v);
            expectName = false;
          }
        }
      }
    }
    // Single-identifier arrow parameter: `x => ...`.
    if (tok.value === '=>') {
      const prev = sig[k - 1];
      if (prev?.type === 'ident' && !RESERVED.has(prev.value)) declared.add(prev.value);
    }
  }

  // Exclude any name that ever appears as an object key or object shorthand,
  // where renaming the reference alone would silently break the code.
  const unsafe = new Set<string>();
  for (let k = 0; k < sig.length; k += 1) {
    const tok = sig[k];
    if (tok.type !== 'ident' || !declared.has(tok.value)) continue;
    const prev = sig[k - 1]?.value;
    const next = sig[k + 1]?.value;
    const shorthand =
      (prev === '{' || prev === ',') &&
      (next === '}' || next === ',') &&
      (prev === '{' || next === '}');
    const key = next === ':';
    if (shorthand || key) unsafe.add(tok.value);
  }

  // Template literals are one opaque token, so `${name}` interpolations are not
  // reachable for renaming. Any name that appears inside a template literal must
  // keep its original spelling everywhere.
  const templateText = tokens
    .filter((t) => t.type === 'template')
    .map((t) => t.value)
    .join('\n');
  if (templateText) {
    for (const name of declared) {
      const re = new RegExp('\\b' + name.replace(/[.*+?^${}()|[\]\\]/g, '\\$&') + '\\b');
      if (re.test(templateText)) unsafe.add(name);
    }
  }

  const existing = new Set(sig.filter((t) => t.type === 'ident').map((t) => t.value));
  const map = new Map<string, string>();
  let counter = 0x1a2b;
  for (const name of declared) {
    if (unsafe.has(name)) continue;
    let generated = '_0x' + counter.toString(16);
    while (existing.has(generated) || map.has(generated)) {
      counter += 1;
      generated = '_0x' + counter.toString(16);
    }
    map.set(name, generated);
    counter += 1;
  }
  return map;
}

function applyRenames(tokens: JsToken[], map: Map<string, string>): void {
  let prevSig: JsToken | null = null;
  for (const tok of tokens) {
    if (tok.type === 'ident' && map.has(tok.value)) {
      const memberAccess = prevSig?.value === '.' || prevSig?.value === '?.';
      if (!memberAccess) {
        tok.value = map.get(tok.value) as string;
      }
    }
    if (isSig(tok)) prevSig = tok;
  }
}

// ---------------------------------------------------------------------------
// Public: obfuscate
// ---------------------------------------------------------------------------

export interface ObfuscateOptions {
  renameLocals: boolean;
  encodeStrings: 'none' | 'hex' | 'base64';
  insertDeadCode: boolean;
  minify: boolean;
}

export const DEFAULT_OBFUSCATE_OPTIONS: ObfuscateOptions = {
  renameLocals: true,
  encodeStrings: 'hex',
  insertDeadCode: false,
  minify: true,
};

function minifyTokens(tokens: JsToken[]): string {
  // Whitespace-only minify that is aware of token boundaries, so we never glue
  // two identifiers/keywords together.
  let out = '';
  let prev: JsToken | null = null;
  for (const tok of tokens) {
    if (tok.type === 'ws' || tok.type === 'line-comment' || tok.type === 'block-comment') continue;
    if (prev) {
      const a = prev.value[prev.value.length - 1] ?? '';
      const b = tok.value[0] ?? '';
      const needsGap =
        (IDENT_PART.test(a) && IDENT_PART.test(b)) ||
        (prev.type === 'punct' && tok.type === 'punct' && '+-'.includes(a) && a === b);
      if (needsGap) out += ' ';
    }
    out += tok.value;
    prev = tok;
  }
  return out;
}

export function obfuscateJs(source: string, options: ObfuscateOptions): string {
  if (!source.trim()) return '';
  const tokens = tokenizeJs(source);

  if (options.renameLocals) {
    const map = collectSafeRenames(tokens);
    applyRenames(tokens, map);
  }

  let usedBase64 = false;
  if (options.encodeStrings !== 'none') {
    let prevSig: JsToken | null = null;
    for (let idx = 0; idx < tokens.length; idx += 1) {
      const tok = tokens[idx];
      if (tok.type === 'string') {
        const value = decodeStringLiteral(tok.value);
        if (value === 'use strict' || value === 'use asm') {
          if (isSig(tok)) prevSig = tok;
          continue;
        }
        const quote = tok.value[0] === "'" ? "'" : '"';
        if (options.encodeStrings === 'hex') {
          tok.value = hexEncodeString(value, quote);
        } else {
          // base64: skip contexts where a call expression is not valid.
          let nextSig: JsToken | null = null;
          for (let j = idx + 1; j < tokens.length; j += 1) {
            if (isSig(tokens[j])) {
              nextSig = tokens[j];
              break;
            }
          }
          const prevVal = prevSig?.value;
          const skip =
            nextSig?.value === ':' ||
            prevVal === 'from' ||
            prevVal === 'import' ||
            prevVal === 'export' ||
            prevVal === '?.';
          if (skip) {
            tok.value = hexEncodeString(value, quote);
          } else {
            tok.value = `_0xdec('${utf8Base64(value)}')`;
            tok.type = 'punct';
            usedBase64 = true;
          }
        }
      }
      if (isSig(tok)) prevSig = tok;
    }
  }

  let body = options.minify ? minifyTokens(tokens) : tokens.map((t) => t.value).join('');
  let prefix = '';
  if (usedBase64) prefix += BASE64_DECODER;
  if (options.insertDeadCode) prefix += DEAD_CODE;
  if (prefix && options.minify) {
    prefix = minifyTokens(tokenizeJs(prefix));
    body = prefix + body;
  } else {
    body = prefix + body;
  }
  return body.trim();
}

// ---------------------------------------------------------------------------
// Public: deobfuscate
// ---------------------------------------------------------------------------

export interface DeobfuscateOptions {
  decodeEscapes: boolean;
  decodeCharCodes: boolean;
  beautify: boolean;
}

export const DEFAULT_DEOBFUSCATE_OPTIONS: DeobfuscateOptions = {
  decodeEscapes: true,
  decodeCharCodes: true,
  beautify: true,
};

function reEncodeMinimal(value: string, quote: string): string {
  let out = quote;
  for (const ch of value) {
    if (ch === quote) out += '\\' + quote;
    else if (ch === '\\') out += '\\\\';
    else if (ch === '\n') out += '\\n';
    else if (ch === '\r') out += '\\r';
    else if (ch === '\t') out += '\\t';
    else {
      const code = ch.codePointAt(0) ?? 0;
      out += code < 32 ? '\\x' + code.toString(16).padStart(2, '0') : ch;
    }
  }
  return out + quote;
}

function decodeCharCodeCalls(source: string): string {
  // Turn String.fromCharCode(72,73) into "HI" where the arguments are all
  // plain integers.
  return source.replace(/String\.fromCharCode\s*\(([^()]*)\)/g, (match, args: string) => {
    const parts = args.split(',').map((p) => p.trim());
    if (parts.length === 0) return match;
    const codes: number[] = [];
    for (const part of parts) {
      if (/^0x[0-9a-fA-F]+$/.test(part)) codes.push(parseInt(part, 16));
      else if (/^\d+$/.test(part)) codes.push(parseInt(part, 10));
      else return match;
    }
    const text = String.fromCharCode(...codes);
    return reEncodeMinimal(text, '"');
  });
}

export function deobfuscateJs(
  source: string,
  options: DeobfuscateOptions,
  beautifier?: (code: string) => string,
): string {
  if (!source.trim()) return '';
  let working = source;

  if (options.decodeCharCodes) {
    working = decodeCharCodeCalls(working);
  }

  if (options.decodeEscapes) {
    const tokens = tokenizeJs(working);
    for (const tok of tokens) {
      if (tok.type === 'string') {
        const quote = tok.value[0] === "'" ? "'" : '"';
        const value = decodeStringLiteral(tok.value);
        tok.value = reEncodeMinimal(value, quote);
      }
    }
    working = tokens.map((t) => t.value).join('');
  }

  if (options.beautify && beautifier) {
    working = beautifier(working);
  }

  return working.trim();
}

export const OBFUSCATE_SAMPLE = `function greet(name) {
  const message = "Hello, " + name + "!";
  console.log(message);
  return message;
}

greet("world");
`;

export const DEOBFUSCATE_SAMPLE = `var _0x1a2b=function(_0x3c){var _0x4d="\\x48\\x65\\x6c\\x6c\\x6f";console.log(_0x4d+_0x3c);};_0x1a2b("\\x20world");`;
